import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { quizService } from '../services/quizService';
import { QuizResultsData, WidgetSettings } from '../types/quiz';
import DimensionChart from '../components/DimensionChart';
import ProfileHeader from '../components/ProfileHeader';
import Top3AreasSection from '../components/Top3AreasSection';
import ResourcesDownload from '../components/ResourcesDownload';

const QuizResultsPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [results, setResults] = useState<QuizResultsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<WidgetSettings>({
    school_name: 'Armenta Learning Academy',
    primary_color: '#4F46E5',
    secondary_color: '#7C3AED',
    accent_color: '#10B981'
  });

  useEffect(() => {
    const fetchResults = async () => {
      if (!token) {
        setError('No results token provided');
        setLoading(false);
        return;
      }

      try {
        // Fetch results
        const data = await quizService.getResults(token);
        setResults(data);

        // Fetch widget settings
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const response = await fetch(`${API_URL}/widget/config`);
        if (response.ok) {
          const settingsData = await response.json();
          setSettings({
            school_name: settingsData.school_name || 'Armenta Learning Academy',
            primary_color: settingsData.primary_color || '#4F46E5',
            secondary_color: settingsData.secondary_color || '#7C3AED',
            accent_color: settingsData.accent_color || '#10B981',
            logo_url: settingsData.logo_url
          });
        }

        setLoading(false);
      } catch (err: any) {
        setError(err.message || 'Failed to load results');
        setLoading(false);
      }
    };

    fetchResults();
  }, [token]);

  // Load Google Analytics (production only)
  useEffect(() => {
    // Only load GA in production (not on localhost)
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      console.log('🔧 Development mode - Google Analytics disabled');
      return;
    }

    // Inject GA script
    const script1 = document.createElement('script');
    script1.src = 'https://www.googletagmanager.com/gtag/js?id=G-42R5PVVB4Z';
    script1.async = true;
    document.head.appendChild(script1);

    const script2 = document.createElement('script');
    script2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'G-42R5PVVB4Z');
    `;
    document.head.appendChild(script2);

    console.log('📊 Google Analytics loaded for quiz results page');

    // Cleanup function to remove scripts when component unmounts
    return () => {
      if (document.head.contains(script1)) document.head.removeChild(script1);
      if (document.head.contains(script2)) document.head.removeChild(script2);
    };
  }, []);

  // Track results page view with profile type
  useEffect(() => {
    if (results && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
      if (typeof (window as any).gtag === 'function') {
        (window as any).gtag('event', 'quiz_results_viewed', {
          event_category: 'Quiz',
          event_label: 'Results Viewed',
          profile: results.primary_profile.name,
          child_name: results.child_name
        });
        console.log('📊 GA Event: quiz_results_viewed -', results.primary_profile.name);
      }
    }
  }, [results]);

  const primaryColor = settings.primary_color || '#4F46E5';

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2" style={{ borderColor: primaryColor }}></div>
      </div>
    );
  }

  // Error state
  if (error || !results) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Results Not Found</h2>
          <p className="text-gray-600 mb-6">{error || 'Unable to load your results'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="container mx-auto px-4 py-8 sm:py-10 md:py-12">
        <div className="max-w-3xl mx-auto">
          {/* Logo */}
          {settings.logo_url && (
            <div className="flex justify-center mb-6 md:mb-8">
              <img src={settings.logo_url} alt={settings.school_name} className="h-12 md:h-16" />
            </div>
          )}

          {/* Section 1: Visual Chart */}
          <DimensionChart
            dimensionScores={results.dimension_scores}
            childName={results.child_name}
          />

          {/* Section 2: Profile Header */}
          <ProfileHeader
            profileName={results.primary_profile.name}
            tagline={results.primary_profile.tagline}
            childName={results.child_name}
            settings={settings}
          />

          {/* Section 3: Your Top 3 Areas (THE VALUE) */}
          <Top3AreasSection
            top3Dimensions={results.top_3_dimensions}
            settings={settings}
          />

          {/* Section 3.5: Resources Download (Conditional) */}
          {results.resources && results.resources.length > 0 && (
            <ResourcesDownload resources={results.resources} />
          )}

          {/* Section 4: Soft CTA */}
          <div className="bg-white rounded-lg shadow-sm p-5 sm:p-6 md:p-7 text-center">
            <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-2 md:mb-3">
              Questions? We're here to help.
            </h3>
            <p className="text-sm md:text-base text-gray-700 mb-4 md:mb-6">
              Want to talk about what {results.child_name} needs? We get it, and we can help.
            </p>
            <div className="flex flex-row gap-4 justify-center items-center">
              <a
                href="/contact?from=quiz"
                onClick={() => {
                  if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
                    if (typeof (window as any).gtag === 'function') {
                      (window as any).gtag('event', 'quiz_cta_clicked', {
                        event_category: 'Quiz',
                        event_label: 'Get in Touch Button',
                        profile: results?.primary_profile.name
                      });
                      console.log('📊 GA Event: quiz_cta_clicked - Get in Touch');
                    }
                  }
                }}
                className="inline-block px-6 py-3 rounded-lg font-bold text-sm md:text-base text-white transition-all hover:shadow-lg"
                style={{
                  background: `linear-gradient(135deg, ${settings.primary_color || '#4F46E5'} 0%, ${settings.secondary_color || '#7C3AED'} 100%)`
                }}
              >
                Get in Touch
              </a>
              <a
                href="https://armentalearningacademy.com"
                onClick={() => {
                  if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
                    if (typeof (window as any).gtag === 'function') {
                      (window as any).gtag('event', 'quiz_home_clicked', {
                        event_category: 'Quiz',
                        event_label: 'Back to Home Button'
                      });
                      console.log('📊 GA Event: quiz_home_clicked');
                    }
                  }
                }}
                className="inline-block px-6 py-3 rounded-lg font-bold text-sm md:text-base text-white transition-all hover:shadow-lg"
                style={{
                  background: `linear-gradient(135deg, ${settings.primary_color || '#4F46E5'} 0%, ${settings.secondary_color || '#7C3AED'} 100%)`
                }}
              >
                Back to Home
              </a>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-8 md:mt-10 text-center">
            <p className="text-gray-500 text-sm md:text-base">
              {settings.school_name} • We see your child. We get it.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizResultsPage;
